import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Building2, RefreshCw, Loader2, MapPin, AlertTriangle, CheckCircle2 } from "lucide-react";

// Contrôle production de la base commune des établissements : présence, géocodage, liaisons par organisation, réprovisionnement idempotent.
export function FacilitiesHealth() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openList, setOpenList] = useState(false);
  const load = () => api.get("/super/facilities/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const provision = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/super/facilities/provision");
      toast.success(`Base commune : ${data.total}/${data.expected} établissements · ${data.created} créé(s) · ${data.geocoded_now} géocodé(s) · ${data.links_added} liaison(s) ajoutée(s)`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  if (!st) return null;
  const ok = st.active >= st.expected && st.geocoded >= st.active;
  const missingLinks = st.organizations.filter((o) => o.active && o.linked < st.active);
  return (
    <div data-testid="facilities-health" className={`rounded-2xl border p-4 ${ok && missingLinks.length === 0 ? "border-green-200 bg-green-50/40" : "border-orange-200 bg-orange-50/40"}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0"><Building2 className="w-5 h-5 text-brand-800" /></div>
          <div className="min-w-0">
            <p className="font-heading font-bold text-brand-900">Base commune des établissements</p>
            <p data-testid="facilities-health-summary" className="text-sm text-slate-600 mt-0.5">
              <b>{st.active}</b>/{st.expected} actifs · <b>{st.geocoded}</b> géolocalisés · {st.organizations.filter((o) => o.active).length} organisation(s)
              {missingLinks.length > 0 && <span className="text-orange-700"> · {missingLinks.length} sans liaison complète</span>}
            </p>
            <p className="text-xs mt-1 flex items-center gap-1">{ok && missingLinks.length === 0 ? <><CheckCircle2 className="w-3.5 h-3.5 text-green-600" /><span className="text-green-700">Provisionnement complet</span></> : <><AlertTriangle className="w-3.5 h-3.5 text-orange-600" /><span className="text-orange-700">Réprovisionnement recommandé (idempotent, aucun doublon)</span></>}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="facilities-toggle-list" variant="outline" size="sm" onClick={() => setOpenList((o) => !o)} className="h-9">{openList ? "Masquer" : "Détail"}</Button>
          <Button data-testid="facilities-provision" size="sm" onClick={provision} disabled={busy} className="h-9 bg-brand-800 hover:bg-brand-900 text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}Réprovisionner</Button>
        </div>
      </div>
      {openList && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1.5 text-xs">
          {st.facilities.map((f) => (
            <div key={f.id} data-testid={`facility-${f.id}`} className="bg-white rounded-lg border border-slate-200 px-3 py-2 flex items-center justify-between gap-2">
              <span className="truncate"><b>{f.short_name}</b> · {f.city}</span>
              <span className={`inline-flex items-center gap-1 shrink-0 ${f.lat != null ? "text-green-700" : "text-orange-600"}`}><MapPin className="w-3 h-3" />{f.lat != null ? `${Number(f.lat).toFixed(4)}, ${Number(f.lng).toFixed(4)}` : "non géolocalisé"}</span>
            </div>
          ))}
          <div className="md:col-span-2 text-slate-500 mt-1">Liaisons : {st.organizations.map((o) => `${o.name} ${o.linked}/${st.active}`).join(" · ")}</div>
        </div>
      )}
    </div>
  );
}
